import React, { useContext, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import classes from './OpportunityPage.module.scss';
import { useRouter } from 'next/router';
import useMixpanel from '@/hooks/useMixpanel';
import ResourcePageHeader from '@/components/ResourcePage/ResourcePageHeader/ResourcePageHeader';
import OverviewSection from './OverviewSection';
import DetailsSection from './DetailsSection';
import WhatToDoNextSection from './WhatToDoNextSection';
import PendingSubmissionBanner from './Banners/PendingSubmissionBanner';
import UnavailableBanner from './Banners/UnavailableBanner';
import HousingHelperBanner from './Banners/HousingHelperBanner';
import UtilizationBanner from './Banners/UtilizationBanner';
import Breadcrumb from './Breadcrumb';
import MoreOpportunitiesSection from '@/components/ResourcePage/MoreOpportunitiesSection/MoreOpportunitiesSection';
import LocationSection from '@/components/ResourcePage/LocationSection/LocationSection';
import ReviewSection from '@/components/ResourcePage/ReviewSection/ReviewSection';
import NoteSection from './NoteSection/NoteSection';
import { toastContext } from '@/context/toast.context';
import { languageContext } from '@/context/language.context';
import ODRS from '@/utils/api/odrs';
import useResourceQueryParamActions from '@/hooks/useResourceQueryParamActions';
import t from '@/utils/Language/UI-translations';
import { searchHelpers } from '@/utils/helpers';

// Render a Breadcrumb trail from the resource's Area > Category > Tag
// We use searchTagLink for both the Tag and Category so the link goes directly to search results
const buildBreadcrumbItems = (resource) => {
  return [
    { label: resource?.areas?.[0], buildUrl: searchHelpers.searchAreaLink },
    { label: resource?.categories?.[0], buildUrl: searchHelpers.searchTagLink },
    { label: resource?.tags?.[0], buildUrl: searchHelpers.searchTagLink }
  ]
    .filter((item) => item.label)
    .map(({ label, buildUrl }) => ({ label, url: buildUrl(label) }));
};

const OpportunityPage = ({ account, resource: initialResource, isPro }) => {
  const [reviewCount, setReviewCount] = useState(null);
  const [translatedResource, setTranslatedResource] = useState(null);
  const { track } = useMixpanel();
  const { addToast } = useContext(toastContext);
  const { currentLanguage } = useContext(languageContext);
  const router = useRouter();

  // Email reminder buttons (?info=correct|incorrect) and resource cards' "Suggest edits"
  // link (?showEditResourceModal=true) land here; the hook acts on those params. It gets
  // initialResource so a translated payload is never submitted.
  useResourceQueryParamActions(initialResource);

  // getServerSideProps always fetches the English payload (the member's language preference
  // lives in localStorage, which the server can't see). Server and first client render both
  // use it, keeping hydration consistent; once the language is known client-side, refetch the
  // resource in that language and swap it in. The id guard keeps a translated payload from a
  // previous opportunity from flashing during client-side navigation.
  const resource = translatedResource?.id === initialResource?.id ? translatedResource : initialResource;

  useEffect(() => {
    if (!initialResource?.id || currentLanguage === 'en') {
      setTranslatedResource(null);
      return;
    }
    let cancelled = false;
    ODRS.getResourceById('opportunities', initialResource.id, { locale: currentLanguage, with_property_labels: true }).then((resp) => {
      if (!cancelled && resp?.data?.id) setTranslatedResource(resp.data);
    });
    return () => {
      cancelled = true;
    };
  }, [currentLanguage, initialResource?.id]);

  // The Angular edit-submission flow redirects here with ?edits_submitted=true because its
  // success alert cannot survive the cross-app page load. Show the toast, then drop the
  // param from the URL so a refresh doesn't repeat it.
  useEffect(() => {
    if (!router.isReady || !router.query.edits_submitted) return;
    addToast({
      type: 'success',
      message: t(
        "Thank you for submitting edits! Your changes are pending until approved by One Degree staff. We'll notify you in about 1-3 days by email once your changes are approved."
      )
    });
    const { edits_submitted, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  }, [router.isReady]);

  useEffect(() => {
    if (!resource?.id) return;
    const props = {
      opportunity_title: resource.title,
      opportunity_id: resource.id,
      opportunity_slug: resource.slug,
      organization_id: resource.organization?.id,
      organization_slug: resource.organization?.slug
    };
    if (router.query.s) props.source = router.query.s;
    track('Opportunity viewed', props);
  }, [resource?.id]);

  return (
    <div className={classes.opportunityPage}>
      <Container className={classes.opportunityPageContainer}>
        <div className={classes.breadcrumbRow}>
          <Breadcrumb items={buildBreadcrumbItems(resource)} />
          <ResourcePageHeader resource={resource} isPro={isPro} />
        </div>
        <div className={classes.pageBanners}>
          <PendingSubmissionBanner resource={resource} />
          <UnavailableBanner opportunity={resource} />
          <HousingHelperBanner opportunity={resource} />
          <UtilizationBanner opportunity={resource} />
        </div>
        <Row className="pb-3">
          <Col lg={8}>
            <OverviewSection resource={resource} reviewCount={reviewCount} showRating showUtilization />
            {/* Below lg the "What to do next" panel sits inline under the overview; at lg+ it
                moves to the sticky sidebar. Both are rendered and toggled with Bootstrap display
                classes so the server and client markup match and no re-render happens when the
                viewport is measured after hydration. */}
            <div className="d-lg-none">
              <WhatToDoNextSection opportunity={resource} />
            </div>
            <DetailsSection opportunity={resource} />
            <LocationSection resource={resource} />
            <ReviewSection resource={resource} account={account} onCountLoaded={setReviewCount} />
            <MoreOpportunitiesSection resource={resource} isPro={isPro} />
            <NoteSection resource={resource} account={account} />
          </Col>
          <Col lg={4} className={`${classes.rightSideBar} d-none d-lg-block`}>
            <WhatToDoNextSection opportunity={resource} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OpportunityPage;
